import { canApproveCommission, commissionCopy } from "@/lib/app/commission-access";
import { useToast } from "@/components/ui/use-toast";
import { useCommissionAccess } from "@/hooks/useCommissionAccess";
import { commissionUsersForScope } from "@/lib/app/commission-access";
import { ChevronRight, FileSpreadsheet } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { saveAs } from "file-saver";
import { useAppStore } from "@/stores";
import {
  getApproved,
  getBadgeVariant,
  getCurrencyFormatted,
  getPaid,
  getUsersList,
  getTimePeriodFromDateRange,
  getUnapproved,
  updateCommissionsStatus,
  getBadgeSummary,
  getUsersWithCommissions,
} from "@/lib/app";
import { useLocation, useParams } from "wouter";
import { ReportCard } from "./components/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  getCommissionsValue,
} from "@revelate/calc/commission";
import { exportToExcel } from "@revelate/export";
import useSWR from "swr";
import { getRoute } from "@/lib/routes";
import { getFullName, getUserInitials } from "@revelate/utils";
import { TableType, get } from "@/lib/supabase";
import {
  CommissionStatus,
  NestedCommission,
  SignedInCompany,
} from "@revelate/types";
import { ErrorWithRetry } from "@/components/ErrorWithRetry";
import { getExchangeRateToCurrency } from "@revelate/calc/commission";
import { useState } from "react";
import { Scope } from "@/dashboard/components/scope";
import { ApproveButton } from "./components/ApproveButton";
import { Loader } from "@/components/Loader";
import { useCommissions } from "@/providers/CommissionsProvider";
import { StatCard } from "@/dashboard/components/stat-card";

function ReportsPageContent() {
  const { data: access, error: accessError, isLoading: accessLoading } = useCommissionAccess();
  const { toast } = useToast();
  const appState = useAppStore((state) => state);
  const { scope, dateRange, currentUser, companyCurrency } = appState;
  const {
    data: company,
    error: companyError,
    isLoading: companyIsLoading,
    mutate: mutateCompany,
  } = useSWR(
    [
      "companies" as TableType,
      currentUser ? currentUser?.company_id?.toString() : null,
    ],
    ([table, companyId]): Promise<SignedInCompany> => get(table, companyId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );
  const { id } = useParams();

  const [updatingCommissionId, setUpdatingCommissionId] = useState<
    string | null
  >(null);

  const scopedUsers = company ? commissionUsersForScope(company, scope, access) : [];

  const {
    users,
    error: commissionsError,
    isLoading: commissionsLoading,
    progress,
  } = useCommissions();

  const scopedUserIds = scopedUsers.map((u) => u.id);

  const commissions =
    users
      ?.filter((user) => scopedUserIds.includes(user.id))
      ?.flatMap((user) => user.commissions) || [];

  const { mutate: mutateCommissions } = useCommissions();

  const updateCommissions = async (
    previousStatus: CommissionStatus,
    newStatus: CommissionStatus,
    filterByCommission?: NestedCommission
  ) => {
    setUpdatingCommissionId(filterByCommission?.id?.toString() || "all");
    try {
      // Update the commissions status
      const updatedCommissions = await updateCommissionsStatus(
        commissions.filter(c => canApproveCommission(access, c.user_id, newStatus !== "approved")),
        previousStatus,
        newStatus,
        filterByCommission
      );

      // Update the client-side data through the provider
      await mutateCommissions(updatedCommissions);
    } catch (error) {
      toast({ title: "Approval could not be saved", description: error instanceof Error ? error.message : "Refresh and try again.", variant: "destructive" });
    } finally {
      setUpdatingCommissionId(null);
    }
  };

  const [, setLocation] = useLocation();

  if (accessError || commissionsError) {
    return (
      <ErrorWithRetry
        error={accessError?.message || commissionsError || "Could not load access"}
        onRetry={() => {
          window.location.reload();
        }}
      />
    );
  }

  if (companyError) {
    return (
      <ErrorWithRetry
        error={companyError.message || "Could not load company"}
        onRetry={() => window.location.reload()}
      />
    );
  }

  if (accessLoading || companyIsLoading || commissionsLoading) {
    return <Loader progress={progress} />;
  }

  return (
    <main className="grid flex-1 items-start gap-4 pt-2 lg:grid-cols-3 xl:grid-cols-5">
      {/* Left */}
      <div className="auto-rows-max items-start gap-4 md:gap-8 lg:col-span-3">
        {/* Header */}
        <div className="flex items-start justify-between pb-6">
          <Scope />
          <ApproveButton
            commissions={commissions}
            updateCommissions={updateCommissions}
            scopedUsers={scopedUsers}
            loading={!!updatingCommissionId || companyIsLoading || commissionsLoading}
          />
        </div>

        {/* Stats */}
        <div className="grid gap-4 grid-cols-3">
          <StatCard title={commissionCopy.totalCommission} size="small">
            {getCurrencyFormatted(
              getCommissionsValue(getUnapproved(commissions)) +
              getCommissionsValue(getApproved(commissions)) +
              getCommissionsValue(getPaid(commissions)),
              companyCurrency?.code
            )}
          </StatCard>
          <StatCard title={commissionCopy.reviewedCommission} size="small">
            {getCurrencyFormatted(
              getCommissionsValue(getApproved(commissions)) +
              getCommissionsValue(getPaid(commissions)),
              companyCurrency?.code
            )}
          </StatCard>
          <StatCard title={commissionCopy.approvedCommission} size="small">
            {getCurrencyFormatted(
              getCommissionsValue(getPaid(commissions)),
              companyCurrency?.code
            )}
          </StatCard>
        </div>

        {/* Employee breakdown */}
        <div className="mt-4">
          <Card x-chunk="dashboard-05-chunk-3" className="relative">
            <CardHeader className="px-7">
              <CardTitle>
                <h2>
                  {getTimePeriodFromDateRange(dateRange)}
                </h2>
              </CardTitle>
              <CardDescription>
                {users?.length || 0} employees have earned commissions in the
                time period.
              </CardDescription>
            </CardHeader>

            {/* Export button */}
            <div className="absolute top-[18px] right-6">
              {currentUser?.role !== "employee" && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-gray-700"
                  loading={!!updatingCommissionId || companyIsLoading || commissionsLoading}
                  onClick={async () => {
                    const fileName = `${company?.name
                      } - ${getTimePeriodFromDateRange(dateRange)}`;

                    const buffer = await exportToExcel({
                      users: users || [],
                    });

                    if (buffer) saveAs(new Blob([buffer]), `${fileName}.xlsx`);
                  }}
                >
                  <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />
                  <span>Export</span>
                </Button>
              )}
            </div>

            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead className="text-right hidden sm:table-cell">
                      {commissionCopy.unapproved}
                    </TableHead>
                    <TableHead className="text-right hidden xl:table-cell">
                      {commissionCopy.approved}
                    </TableHead>
                    <TableHead className="text-right hidden xl:table-cell">
                      {commissionCopy.paid}
                    </TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users &&
                    getUsersWithCommissions(users)?.map((user, index) => {
                      return (
                        <TableRow
                          className={
                            user.id === scope?.value ? "bg-accent" : ""
                          }
                          key={index}
                        >
                          <TableCell>
                            <div className="flex flex-row gap-x-4">
                              <Avatar className="h-9 w-9">
                                <AvatarImage
                                  src={user.avatar_url || ""}
                                  alt="Avatar"
                                />
                                <AvatarFallback>
                                  {getUserInitials(user)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="flex flex-row gap-x-1 items-center">
                                  <span className="font-medium">
                                    {getFullName(user)}
                                  </span>
                                  {getCommissionsValue(user.commissions) > 0 && (
                                    <Badge
                                      className="text-[10px] py-0 px-1.5 h-4 shrink-0"
                                      variant={
                                        getCommissionsValue(user.commissions) > 0
                                          ? getBadgeVariant(user.commissions)
                                          : "secondary"
                                      }
                                    >
                                      {getBadgeSummary(user.commissions)}
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {user.email}
                                </div>
                              </div>
                            </div>
                          </TableCell>

                          <TableCell className="text-right hidden sm:table-cell">
                            <div className="font-medium">
                              {getCurrencyFormatted(
                                getCommissionsValue(
                                  getUnapproved(user.commissions)
                                ),
                                companyCurrency?.code
                              )}
                            </div>
                            {user?.currency &&
                              company?.default_currency_id !==
                              user?.currency?.id && (
                                <div className="hidden text-sm text-muted-foreground md:inline">
                                  {getCurrencyFormatted(
                                    getCommissionsValue(
                                      getUnapproved(user.commissions)
                                    ) /
                                    getExchangeRateToCurrency(
                                      user.currency,
                                      company
                                    ),
                                    user.currency?.code
                                  )}
                                </div>
                              )}
                          </TableCell>

                          <TableCell className="text-right hidden xl:table-cell">
                            <div className="font-medium">
                              {getCurrencyFormatted(
                                getCommissionsValue(
                                  getApproved(user.commissions)
                                ),
                                companyCurrency?.code
                              )}
                            </div>
                            {user?.currency &&
                              company?.default_currency_id !==
                              user?.currency?.id && (
                                <div className="hidden text-sm text-muted-foreground md:inline">
                                  {getCurrencyFormatted(
                                    getCommissionsValue(
                                      getApproved(user.commissions)
                                    ) /
                                    getExchangeRateToCurrency(
                                      user.currency,
                                      company
                                    ),
                                    user.currency?.code
                                  )}
                                </div>
                              )}
                          </TableCell>
                          <TableCell className="text-right hidden xl:table-cell">
                            <div className="font-medium">
                              {getCurrencyFormatted(
                                getCommissionsValue(getPaid(user.commissions)),
                                companyCurrency?.code
                              )}
                            </div>
                            {user?.currency &&
                              company?.default_currency_id !==
                              user?.currency?.id && (
                                <div className="hidden text-sm text-muted-foreground md:inline">
                                  {getCurrencyFormatted(
                                    getCommissionsValue(
                                      getPaid(user.commissions)
                                    ) /
                                    getExchangeRateToCurrency(
                                      user.currency,
                                      company
                                    ),
                                    user.currency?.code
                                  )}
                                </div>
                              )}
                          </TableCell>

                          <TableCell className="w-6">
                            <Button
                              onClick={() =>
                                setLocation(
                                  getRoute("reports", "user", user.id)
                                )
                              }
                              variant="outline"
                              size="icon-sm"
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right */}
      <div className="md:col-span-1 lg:col-span-2">
        <ReportCard
          employees={users && users.length > 0 ? getUsersList(users) : []}
          timePeriod={getTimePeriodFromDateRange(dateRange)}
          ids={
            id
              ? [id.toString()]
              : scopedUsers && scopedUsers.length > 0
                ? scopedUsers?.map((u) => u.id)
                : []
          }
          commissions={commissions}
          updateCommissions={updateCommissions}
          updatingCommissionId={updatingCommissionId}
          scopedUsers={scopedUsers}
          dateRange={dateRange}
        />
      </div>
    </main>
  );
}

export function ReportsPage() {
  return <ReportsPageContent />;
}
